import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api/axios";
import DaumPostcode from "../../components/widgets/DaumPostcode";
import TmapGeocoder from "../../components/viewer/tmap/TmapGeocoder";
import ConfirmDialog from "../../components/common/ui/ConfirmModal";

export default function ProjectForm({ initialData }) {
  // 모듈 목록 (label: 화면표시, key: 백엔드 전송값)
  const MODULE_OPTIONS = [
    { key: "overview", label: "사업개요" },
    { key: "permit", label: "인허가" },
    { key: "contract", label: "계약/비용관리" },
    { key: "design", label: "설계관리" },
    { key: "material", label: "자재관리" },
    { key: "process", label: "공정관리" },
    { key: "construction", label: "시공관리" },
    { key: "quality", label: "품질관리" },
    { key: "safety", label: "안전관리" },
    { key: "meeting", label: "회의∙공지" },
    { key: "approval", label: "전자결재" },
    { key: "document", label: "자료실" },
    { key: "pcmodule", label: "PCModule" },
    { key: "fourd", label: "4dSimulation" },
  ];

  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState({
    id: "",
    name: "",
    start_date: "",
    end_date: "",
    address: "",
    extra_address: "",
    detail_address: "",
    lat_coord: null,
    lon_coord: null,
    modules_ordered: [],
    safe_date: "",
  });

  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (initialData) {
      setProject((prev) => ({
        ...prev,
        ...initialData,
        modules_ordered: Array.isArray(initialData.modules_ordered)
          ? initialData.modules_ordered
          : [], // undefined 방지
      }));
    }
  }, [initialData]);

  const handlePjtIdChange = (e) => {
    // 영문, 숫자, - 만 허용
    const filtered = e.target.value.replace(/[^A-Za-z0-9-]/g, "");
    setProject((prev) => ({ ...prev, id: filtered }));
  };

  const validateProject = () => {
    if (!project.id || project.id.length < 3) {
      alert("프로젝트 코드는 3자 이상 입력해야 합니다.");
      return false;
    }
    if (!project.name) {
      alert("프로젝트 이름은 3자 이상 입력해야 합니다.");
      return false;
    }
    if (!project.start_date) {
      alert("프로젝트 시작일을 입력해 주세요.");
      return false;
    }
    if (!project.end_date) {
      alert("프로젝트 종료일을 입력해 주세요.");
      return false;
    }
    if (new Date(project.start_date) > new Date(project.end_date)) {
      alert("프로젝트 종료일은 시작일 이후여야 합니다.");
      return false;
    }
    if (!project.address) {
      alert("프로젝트 주소를 입력해 주세요.");
      return false;
    }
    console.log("프로젝트 유효성 검사 통과:", project);
    return true;
  };

  const upsertProjectsInStorage = (newProj) => {
    const list = JSON.parse(localStorage.getItem("projects")) || [];
    const dedup = [newProj, ...list.filter((p) => p.id !== newProj.id)];
    localStorage.setItem("projects", JSON.stringify(dedup));
  };

  // ✅ 등록 함수
  const handleRegister = async () => {
    console.log("등록 클릭, 프로젝트 데이터:", project);
    if (!validateProject(project)) return;

    const payload = {
      ...project,
      modules: project.modules_ordered, // 백엔드용 키 맞춰서 전송
    };

    try {
      if (!projectId) {
        // 신규 등록
        console.log("신규 프로젝트 등록, 페이로드:", payload);
        const res = await api.post("project/info/", payload);
        if (res.status === 201) {
          const proj = res.data.project;
          const needsCompany = !!res.data.needs_company;

          // 로컬 상태/저장소 갱신
          const newProj = { id: proj.id, name: proj.name };
          upsertProjectsInStorage(newProj);
          localStorage.setItem("projectId", proj.id);
          localStorage.setItem("projectName", proj.name);

          // 서버 플래그 있으면 그걸로 분기
          if (needsCompany) {
            alert(
              "프로젝트가 등록되었습니다. 이어서 회사 정보를 입력해 주세요."
            );
            navigate(`/project/${proj.id}/system/company/add`);
            return;
          }

          // 없으면 요약 API로 멤버십 체크 후 분기
          try {
            const ms = await api.get(`membership/me/?summary=1`);
            if (!ms.data.has_company) {
              navigate(`/project/${proj.id}/system/user/company/`);
              return;
            }
          } catch (err) {
            console.log("등록 실패:", err);
            alert("프로젝트 등록에 실패했습니다.");
          }

          setConfirmOpen(true);
        }
      } else {
        // 수정(PUT: 전체 갱신, PATCH: 부분 갱신)
        await api.put(`project/info/${projectId}/`, payload);
        alert("프로젝트 정보가 수정되었습니다.");
      }
    } catch (err) {
      console.error("저장 실패:", err);
      alert("저장에 실패했습니다.");
    }
  };

  const goDetail = () => {
    setConfirmOpen(false);
    if (project.id) {
      localStorage.setItem("projectId", project.id);
      navigate(`/project/${project.id}/`);
    }
  };

  const goList = () => {
    setConfirmOpen(false);
    navigate("/select-project/");
    console.log(localStorage.getItem("projects"));
  };

  return (
    <>
      <div className="max-w-5xl mx-auto mt-10 bg-white p-8 rounded-xl shadow">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* 왼쪽: 프로젝트 기본 정보 */}
          <div className="space-y-6">
            {/* 프로젝트 코드 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                프로젝트 코드
              </label>
              <input
                type="text"
                value={project.id}
                onChange={handlePjtIdChange}
                placeholder="영문, 숫자, - 만 입력 가능 (3자 이상)"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            {/* 프로젝트 이름 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                프로젝트 이름
              </label>
              <input
                type="text"
                value={project.name || ""}
                onChange={(e) =>
                  setProject((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="프로젝트 이름을 입력하세요"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            {/* 기간 */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  시작일
                </label>
                <input
                  type="date"
                  value={project.start_date || ""}
                  onChange={(e) =>
                    setProject((prev) => ({
                      ...prev,
                      start_date: e.target.value,
                      safe_date: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  종료일
                </label>
                <input
                  type="date"
                  value={project.end_date || ""}
                  onChange={(e) =>
                    setProject((prev) => ({
                      ...prev,
                      end_date: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
            </div>

            {/* 주소 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                프로젝트 주소
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="주소(지번/도로명 입력 가능)"
                  value={project.address || ""}
                  onChange={(e) =>
                    setProject((prev) => ({ ...prev, address: e.target.value }))
                  }
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <DaumPostcode
                  text="주소 검색"
                  address={project.address || ""}
                  extraAddress={project.extra_address || ""}
                  onAddressChange={({ address, extra_address }) => {
                    setProject((prev) => ({ ...prev, address, extra_address }));
                  }}
                />
              </div>
              <input
                type="text"
                placeholder="참고 주소"
                value={project.extra_address || ""}
                readOnly
                className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50"
              />
              <input
                id="detailAddress"
                type="text"
                placeholder="상세 주소"
                value={project.detail_address || ""}
                onChange={(e) =>
                  setProject((prev) => ({
                    ...prev,
                    detail_address: e.target.value,
                  }))
                }
                className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            {/* 지도 */}
            <div>
              <TmapGeocoder
                address={project.address}
                onGeocode={({ lat, lon }) => {
                  setProject((prev) => ({
                    ...prev,
                    lat_coord: lat,
                    lon_coord: lon,
                  }));
                }}
                mapId={"map_div_1"}
              />
            </div>
          </div>

          {/* 오른쪽: 모듈 관리 */}
          <div className="space-y-4 border-l pl-6">
            <h2 className="text-lg font-semibold text-gray-800">모듈 관리</h2>
            <p className="text-sm text-gray-500">사용할 모듈을 선택하세요.</p>

            <div className="space-y-2">
              {MODULE_OPTIONS.map(({ key, label }) => (
                <label
                  key={key}
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={project.modules_ordered?.includes(key) || false}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setProject((prev) => {
                        const current = prev.modules_ordered || [];
                        console.log("현재 선택된 모듈:", current);
                        return {
                          ...prev,
                          modules_ordered: isChecked
                            ? [...current, key]
                            : current.filter((m) => m !== key),
                        };
                      });
                    }}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* 등록 버튼 */}
        <div className="mt-8 text-right">
          <button
            onClick={handleRegister}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
          >
            등록
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="프로젝트가 등록되었습니다."
        description="생성한 프로젝트 페이지로 이동하시겠습니까?"
        confirmText="이동"
        cancelText="목록으로"
        onConfirm={goDetail}
        onCancel={goList}
      />
    </>
  );
}
